import { hash2i } from "../core/math.js";
import { CFG } from "./config.js";
import { mapById } from "./maps.js";

export function worldToCell(x, y) {
  return { cx: Math.floor(x / CFG.cellPx), cy: Math.floor(y / CFG.cellPx) };
}

export function cellToWorldCenter(cx, cy) {
  return { x: (cx + 0.5) * CFG.cellPx, y: (cy + 0.5) * CFG.cellPx };
}

export function wallKey(cx, cy) {
  return `${cx},${cy}`;
}

function hash01(cx, cy, salt = 0) {
  const h = hash2i(cx + salt * 1013, cy - salt * 3251);
  return (h % 100000) / 100000;
}

function sampleTileBase(mapId, cx, cy) {
  const map = mapById(mapId);
  // Only "ice" biome remains; mud removed.
  const t = map.tiles || { wall: 0.06, ice: 0.04 };
  const iceP = t.ice || 0;

  const rWall = hash01(cx, cy, 1);
  const rBiome = hash01(cx, cy, 2);
  const rDetail = hash01(cx, cy, 3);

  // keep a safe area near origin (spawn)
  if (Math.abs(cx) <= 2 && Math.abs(cy) <= 2) {
    return { wall: false, biome: "normal", glyph: " " };
  }

  // Light "designed" walls for Hell: diagonal stripes with deterministic gaps.
  if (mapId === "hell") {
    const stripe = ((cx + cy) % 17 + 17) % 17;
    if (stripe === 0) {
      // keep some gaps to avoid hard locks
      if (rDetail > 0.22) {
        const g = rDetail < 0.7 ? "#" : "+";
        return { wall: true, biome: "normal", glyph: g };
      }
    }
  }

  const wall = rWall < t.wall;
  if (wall) {
    // keep wall glyph "thin" to avoid overwhelming the scene
    const g = rDetail < 0.7 ? "#" : "+";
    return { wall: true, biome: "normal", glyph: g };
  }

  // Winter is full "verglas": always slippery (except safe spawn area above).
  if (mapId === "winter") {
    const glyph = rDetail < 0.55 ? "~" : " ";
    return { wall: false, biome: "ice", glyph };
  }

  // biome: ice (otherwise normal)
  let biome = "normal";
  if (rBiome < iceP) biome = "ice";

  // subtle biome hint glyphs (sparse)
  let glyph = " ";
  if (biome === "ice" && rDetail < 0.25) glyph = "~";

  return { wall: false, biome, glyph };
}

export function wallHpForMap(mapId) {
  if (mapId === "hell") return 88;
  if (mapId === "winter") return 112;
  if (mapId === "classic") return 132;
  return 140;
}

export function sampleTile(mapId, cx, cy, brokenWalls) {
  const tile = sampleTileBase(mapId, cx, cy);
  if (!tile.wall) return tile;
  if (!brokenWalls || !brokenWalls.has(wallKey(cx, cy))) return tile;
  // Broken walls leave subtle rubble so players can read destroyed paths.
  const rubble = hash01(cx, cy, 9) < 0.5 ? "." : ",";
  return { wall: false, biome: "normal", glyph: rubble };
}

export function damageWallCell({ mapId, cx, cy, damage, brokenWalls, wallDamage }) {
  if (!damage || damage <= 0) return false;
  if (!brokenWalls || !wallDamage) return false;

  const key = wallKey(cx, cy);
  if (brokenWalls.has(key)) return false;

  const tile = sampleTileBase(mapId, cx, cy);
  if (!tile.wall) return false;

  const hp0 = wallDamage.get(key) ?? wallHpForMap(mapId);
  const hp1 = hp0 - damage;
  if (hp1 <= 0) {
    wallDamage.delete(key);
    brokenWalls.add(key);
    return true;
  }

  wallDamage.set(key, hp1);
  return false;
}

export function damageWallAtWorld({ mapId, x, y, damage, brokenWalls, wallDamage }) {
  const c = worldToCell(x, y);
  return damageWallCell({ mapId, cx: c.cx, cy: c.cy, damage, brokenWalls, wallDamage });
}

export function resolveCircleVsWalls({ mapId, x, y, r, brokenWalls }) {
  // iterative push-out against neighboring wall cells
  let px = x;
  let py = y;
  const { cx, cy } = worldToCell(px, py);
  for (let it = 0; it < 2; it++) {
    for (let oy = -1; oy <= 1; oy++) {
      for (let ox = -1; ox <= 1; ox++) {
        const tx = cx + ox;
        const ty = cy + oy;
        const tile = sampleTile(mapId, tx, ty, brokenWalls);
        if (!tile.wall) continue;

        // AABB of the tile in world
        const minX = tx * CFG.cellPx;
        const minY = ty * CFG.cellPx;
        const maxX = minX + CFG.cellPx;
        const maxY = minY + CFG.cellPx;

        // closest point on AABB to circle center
        const qx = Math.max(minX, Math.min(maxX, px));
        const qy = Math.max(minY, Math.min(maxY, py));
        const dx = px - qx;
        const dy = py - qy;
        const d2 = dx * dx + dy * dy;
        if (d2 <= r * r && d2 > 0.000001) {
          const d = Math.sqrt(d2);
          const push = (r - d) + 0.25;
          px += (dx / d) * push;
          py += (dy / d) * push;
        } else if (d2 <= 0.000001) {
          // inside corner case: push out arbitrarily
          px += 0.5;
          py += 0.5;
        }
      }
    }
  }
  return { x: px, y: py };
}
